package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.auth.CompensationDto
import com.evoapps.evoshift.data.dto.auth.CompensationType
import com.evoapps.evoshift.data.dto.auth.ContractPeriodDto
import com.evoapps.evoshift.domain.model.ContractPeriod
import com.evoapps.evoshift.data.dto.auth.PayrollProfileDto
import com.evoapps.evoshift.data.dto.auth.ProfilePeriodDto
import com.evoapps.evoshift.data.dto.auth.RatePeriodDto
import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.market.payroll.ProfilePeriod
import com.evoapps.evoshift.data.dto.auth.SensitiveGeneralDto
import com.evoapps.evoshift.data.dto.auth.SensitiveWorkplaceDto
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.RatePeriod
import com.evoapps.evoshift.domain.model.SensitiveUserData

class SensitiveDataMapper {

    // --- Z BAZY (DTO) NA APLIKACJĘ (DOMAIN) ---
    // Łączy dane ogólne (generalDto) i mapę danych firmowych (workplaceDtos) w jeden obiekt
    fun toDomain(
        userId: String,
        generalDto: SensitiveGeneralDto,
        workplaceDtos: Map<String, SensitiveWorkplaceDto>
    ): SensitiveUserData {

        // Przetwarzamy mapę DTO na mapę obiektów domenowych
        val financialMap = workplaceDtos.mapValues { (_, dto) ->
            FinancialEngagement(
                contractTypeId = dto.contractTypeId ?: "Unknown",
                declaredNumberOfHours = dto.declared_number_of_hours,
                compensation = mapCompensationToDomain(dto.compensation),
                payrollProfile = dto.payrollProfile?.let(::profileToDomain) ?: PayrollProfile(),
                compensationEffectiveFrom = dto.compensationEffectiveFrom?.takeIf { it.isNotBlank() },
                rateHistory = dto.rateHistory.map { RatePeriod(it.effectiveFrom, mapCompensationToDomain(it.compensation)) },
                payrollId = dto.payrollId?.trim()?.takeIf { it.isNotEmpty() },
                // SH-149: older documents have neither: their one profile applies from the start.
                payrollProfileEffectiveFrom = dto.payrollProfileEffectiveFrom?.takeIf { it.isNotBlank() },
                profileHistory = dto.profileHistory.map { ProfilePeriod(it.effectiveFrom, profileToDomain(it.profile)) },
                // SH-212: older documents have neither: their one contract applies from the start.
                contractEffectiveFrom = dto.contractEffectiveFrom?.takeIf { it.isNotBlank() },
                contractHistory = dto.contractHistory.map {
                    ContractPeriod(it.effectiveFrom, it.contractTypeId ?: "Unknown", it.declared_number_of_hours)
                }
            )
        }

        return SensitiveUserData(
            userId = userId,
            email = generalDto.email,
            phoneNumber = generalDto.phoneNumber,
            address = generalDto.address,
            bankAccountNumber = generalDto.bankAccountNumber,
            financialEngagements = financialMap
        )
    }

    private fun profileToDomain(dto: PayrollProfileDto) = PayrollProfile(
        dto.under26, dto.student, dto.taxReliefRequested, dto.commutes, dto.voluntarySickness, dto.insuredElsewhereAtMinimumWage,
        over50 = dto.over50, longService = dto.longService, ppk = dto.ppk, ppkReducedRate = dto.ppkReducedRate,
        under26Until = dto.under26Until?.takeIf { it.isNotBlank() }, studentUntil = dto.studentUntil?.takeIf { it.isNotBlank() }
    )

    private fun profileToDto(profile: PayrollProfile) = with(profile) {
        PayrollProfileDto(
            under26, student, taxReliefRequested, commutes, voluntarySickness, insuredElsewhereAtMinimumWage,
            over50 = over50, longService = longService, ppk = ppk, ppkReducedRate = ppkReducedRate,
            under26Until = under26Until?.takeIf { it.isNotBlank() }, studentUntil = studentUntil?.takeIf { it.isNotBlank() }
        )
    }

    // Pomocnicza: Zamiana płaskiego DTO na Sealed Class
    private fun mapCompensationToDomain(dto: CompensationDto?): CompensationModel {
        // Fallback: jeśli w bazie brakuje obiektu compensation, zakładamy 0.0/h
        if (dto == null) return CompensationModel.Hourly(rate = 0.0, employerCost = 0.0)

        return when (dto.type) {
            CompensationType.HOURLY -> CompensationModel.Hourly(
                rate = dto.hourlyRate ?: 0.0,
                employerCost = dto.employerCost,
                currency = dto.currency
            )
            CompensationType.MONTHLY_FIXED -> CompensationModel.MonthlyFixed(
                baseSalary = dto.monthlyBaseSalary ?: 0.0,
                fte = dto.fte ?: 1.0,
                employerCost = dto.employerCost,
                currency = dto.currency
            )
        }
    }

    // --- Z APLIKACJI (DOMAIN) NA BAZĘ (DTO) ---
    // Używane przy zapisie/aktualizacji danych dla konkretnego miejsca pracy
    fun toWorkplaceDto(domain: FinancialEngagement): SensitiveWorkplaceDto {

        return SensitiveWorkplaceDto(
            contractTypeId = domain.contractTypeId,
            declared_number_of_hours = domain.declaredNumberOfHours,
            compensation = compensationToDto(domain.compensation),
            payrollProfile = profileToDto(domain.payrollProfile),
            compensationEffectiveFrom = domain.compensationEffectiveFrom,
            rateHistory = domain.rateHistory.map { RatePeriodDto(it.effectiveFrom, compensationToDto(it.compensation)) },
            payrollId = domain.payrollId?.trim()?.takeIf { it.isNotEmpty() },
            payrollProfileEffectiveFrom = domain.payrollProfileEffectiveFrom,
            profileHistory = domain.profileHistory.map { ProfilePeriodDto(it.effectiveFrom, profileToDto(it.profile)) },
            contractEffectiveFrom = domain.contractEffectiveFrom,
            contractHistory = domain.contractHistory.map { ContractPeriodDto(it.effectiveFrom, it.contractTypeId, it.declaredNumberOfHours) }
        )
    }

    private fun compensationToDto(compensation: CompensationModel): CompensationDto {
        // Konwersja Sealed Class na płaskie DTO z Enumem
        return when (val c = compensation) {
            is CompensationModel.Hourly -> CompensationDto(
                type = CompensationType.HOURLY,
                hourlyRate = c.rate,
                employerCost = c.employerCost,
                currency = c.currency,
                // Reszta pól (monthlyBaseSalary, fte) zostaje null
            )
            is CompensationModel.MonthlyFixed -> CompensationDto(
                type = CompensationType.MONTHLY_FIXED,
                monthlyBaseSalary = c.baseSalary,
                fte = c.fte,
                employerCost = c.employerCost,
                currency = c.currency
                // hourlyRate zostaje null
            )
        }
    }
}